import { IconArrowInbox } from 'central-icons/IconArrowInbox';
import { IconChainLink4 } from 'central-icons/IconChainLink4';
import { IconChart2 } from 'central-icons/IconChart2';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { IconExpand45 } from 'central-icons/IconExpand45';
import { IconImageSparkle } from 'central-icons/IconImageSparkle';
import { IconMagnifyingGlass } from 'central-icons/IconMagnifyingGlass';
import { IconWindowCursor } from 'central-icons/IconWindowCursor';
import classNames from 'classnames';

import { FeatureFlag } from '@revelio/core';
import { IconButton, Menu, MenuButton, MenuItem, MenuList } from '@revelio/ui';

import { MenuItemWithDisabledReason } from '../../../../../shared/ui/menu-item/MenuItemWithDisabledReason';
import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import styles from './ChartActionMenu.module.css';

export type MenuOptions =
  | 'expand-plot'
  | 'share-plot'
  | 'download-data'
  | 'download-png'
  | 'download-ppt';

export const ChartActionMenuDropdown = ({
  menu,
  slim,
  className,
  notSharableReason,
  downloadDataDisabled,
  downloadPNGDisabled,
  downloadPPTDisabled,
  previewPPTDisabled,
  onExpandPlotClick,
  onSharePlotClick,
  onDownloadDataClick,
  onDownloadPNGClick,
  onDownloadPPTClick,
  onPreviewPPTClick,
  onInspectDataClick,
}: {
  slim?: boolean;
  /** Extra class on the trigger, for hosts that size or place it themselves. */
  className?: string;
  menu: MenuOptions[];
  notSharableReason: string | null;
  downloadDataDisabled?: boolean;
  downloadPNGDisabled?: boolean;
  downloadPPTDisabled?: boolean;
  previewPPTDisabled?: boolean;
  onExpandPlotClick: () => void;
  onSharePlotClick: () => void;
  onDownloadDataClick: () => void;
  onDownloadPNGClick: () => void;
  onDownloadPPTClick: () => void;
  onPreviewPPTClick: () => void;
  /** Only supplied while Data Inspector mode is on; absent hides the entry. */
  onInspectDataClick?: () => void;
}) => {
  const isPPTPreviewEnabled = useUnleashFlag(FeatureFlag.PPTPreview);

  return (
    <Menu>
      <MenuButton>
        <IconButton
          aria-label="Chart actions"
          variant="ghost"
          shape="squared"
          size="sm"
          icon={<IconDotGrid1x3Vertical size={14} />}
          data-testid="action-menu-button"
          className={classNames(
            styles.menuButton,
            slim && styles.slim,
            className
          )}
        />
      </MenuButton>
      <MenuList align="end" data-testid="action-menu-list">
        {menu.includes('expand-plot') && (
          <MenuItem
            icon={<IconExpand45 size={14} />}
            onClick={onExpandPlotClick}
          >
            Expand Plot
          </MenuItem>
        )}
        {menu.includes('share-plot') && (
          <MenuItemWithDisabledReason
            icon={<IconChainLink4 size={14} />}
            onClick={onSharePlotClick}
            isDisabled={notSharableReason !== null}
            disabledReason={notSharableReason ?? ''}
          >
            Share Plot
          </MenuItemWithDisabledReason>
        )}
        {onInspectDataClick && (
          <MenuItem
            icon={<IconMagnifyingGlass size={14} />}
            onClick={onInspectDataClick}
            data-testid="inspect-data-menu-item"
          >
            Data source
          </MenuItem>
        )}
        {menu.includes('download-data') && (
          <MenuItemWithDisabledReason
            icon={<IconArrowInbox size={14} />}
            onClick={onDownloadDataClick}
            isDisabled={downloadDataDisabled}
            disabledReason="Available on paid plans"
          >
            Download Data
          </MenuItemWithDisabledReason>
        )}
        {menu.includes('download-png') && (
          <MenuItemWithDisabledReason
            icon={<IconImageSparkle size={14} />}
            onClick={onDownloadPNGClick}
            isDisabled={downloadPNGDisabled}
            disabledReason="Available on paid plans"
          >
            Download PNG
          </MenuItemWithDisabledReason>
        )}
        {menu.includes('download-ppt') && (
          <>
            <MenuItemWithDisabledReason
              icon={<IconChart2 size={14} />}
              onClick={onDownloadPPTClick}
              isDisabled={downloadPPTDisabled}
              disabledReason="Available on paid plans"
            >
              Download PPT
            </MenuItemWithDisabledReason>
            {isPPTPreviewEnabled && (
              <MenuItemWithDisabledReason
                icon={<IconWindowCursor size={14} />}
                onClick={onPreviewPPTClick}
                isDisabled={previewPPTDisabled}
                disabledReason="Available on paid plans"
              >
                Preview PPT
              </MenuItemWithDisabledReason>
            )}
          </>
        )}
      </MenuList>
    </Menu>
  );
};
